import { useRef, useState } from 'react';

import IconButton from 'components/IconButton';
import Img from 'components/Img';
import { useDebouncedCallback, useEffectOnce, useElementResize } from 'hooks';
import { cn, cnm } from 'utils/classNames';
import { useI18n } from 'utils/i18n';

import {
	getPointerPositionPoint,
	getRenderedImageSize,
	getScrollPositionPoint,
	getZoomInWidth,
	getZoomOutWidth,
	type NormalizedPoint,
	pan,
	scrollToMousePosition,
	useContainerDimensionsRef,
	useScrollPositionStatus,
} from './helpers';
import ImageZoomViewControls from './ImageZoomViewControls';

interface Props {
	alt: string | undefined;
	className?: string;
	height: number;
	imageContainerClassName?: string;
	innerContainerClassName?: string;
	onZoomStart?: () => void;
	onZoomStop?: () => void;
	src: string;
	width: number;
}

/**
 * Image with zoom controls.
 *
 * The zoomed in view is contained by regular overflow, which allows touch
 * devices in particular to feel native when scrolling. Has several different
 * ways of controlling the view:
 *
 * - Zoom in and out on click (or touch tap).
 * - Scroll the zoomed view natively (scroll wheel, touch panning and keyboard
 *   arrows when the container is focused).
 * - Pan the zoomed view with the mouse (image position follows the cursor).
 * - When zoomed, button controls for zooming and panning are shown.
 * - Keyboard shortcuts (+/-/arrows/escape).
 */
export default function ImageZoomView({
	alt,
	className,
	height,
	imageContainerClassName,
	innerContainerClassName,
	onZoomStart,
	onZoomStop,
	src,
	width,
}: Props) {
	const { t } = useI18n();
	const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
	const [hasControls, setHasControls] = useState(false);
	const isZoomed = maxWidth !== undefined;
	const isFullyZoomed = isZoomed && Boolean(maxWidth >= width);

	const imageRef = useRef<HTMLImageElement>(null);
	const floatingButtonRef = useRef<HTMLButtonElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const [containerDimensionsRef, updateContainerDimensions] =
		useContainerDimensionsRef(scrollContainerRef);
	const {
		isAtBottom,
		isAtLeft,
		isAtRight,
		isAtTop,
		updateScrollPositionStatus,
	} = useScrollPositionStatus(scrollContainerRef, containerDimensionsRef);

	const scrollContainerToPoint = (point: NormalizedPoint) => {
		// This is done on zoom, so wait for the browser to calculate the updated
		// container dimensions based on the new image size before reading them
		// and scrolling.
		requestAnimationFrame(() => {
			updateContainerDimensions();
			const { scrollRangeX, scrollRangeY } = containerDimensionsRef.current;
			scrollContainerRef.current?.scrollTo(
				scrollRangeX * point.x,
				scrollRangeY * point.y,
			);
		});
	};

	const resetZoom = () => {
		setMaxWidth(undefined);
		setHasControls(false);
		// iOS Safari can keep a scroll position even if it's larger than the
		// container and the container isn't even scrollable, so reset to be sure.
		scrollContainerToPoint({ x: 0, y: 0 });
		onZoomStop?.();
	};

	// Update container dimensions and scroll position status on initial render
	// and when the container is resized. Minimal debounce to guard against
	// simultaneous calls, useElementResize already has its own debounce.
	const [updateLayoutData] = useDebouncedCallback(() => {
		updateContainerDimensions();
		updateScrollPositionStatus();
		// If dimensions collapse, the zoom view has likely been hidden.
		// Reset zoomed state to not have it active when displayed again.
		const { outerHeight, outerWidth } = containerDimensionsRef.current;
		if (isZoomed && outerWidth === 0 && outerHeight === 0) {
			resetZoom();
		}
	}, 10);
	useEffectOnce(updateLayoutData);
	useElementResize(() => scrollContainerRef.current, updateLayoutData);

	const panContainer = (direction: 'down' | 'left' | 'right' | 'up') => {
		const size = containerDimensionsRef.current;
		pan({
			direction,
			// One third of the container width or height, whichever is smaller.
			// Use the same distance for all directions for a consistent feel.
			// Fall back to 200 in case the dimensions are zero for some reason.
			distance: Math.min(size.outerHeight, size.outerWidth) / 3 || 200,
			element: scrollContainerRef.current,
		});
	};

	const getContainerScrollPosition = () =>
		scrollContainerRef.current
			? getScrollPositionPoint(
					scrollContainerRef.current,
					containerDimensionsRef.current,
				)
			: undefined;

	const getRenderedImageWidth = () =>
		imageRef.current
			? getRenderedImageSize(imageRef.current, width, height).width
			: undefined;

	const zoomIn = (clickEvent?: MouseEvent) => {
		const currentWidth = maxWidth ?? getRenderedImageWidth();
		if (!currentWidth) {
			return;
		}

		const newMaxWidth = getZoomInWidth({
			currentMaxWidth: currentWidth,
			fullImageWidth: width,
		});
		if (newMaxWidth === maxWidth) {
			return;
		}
		setMaxWidth(newMaxWidth);
		setHasControls(true);

		// When a scrollable area is expanded, the browser will keep the current
		// scroll position and expand right and down. To keep the relative scroll
		// position and the feel of zooming in on a focus point, scroll to the
		// click position if available or try to keep the current relative scroll
		// position (e.g. scrolled 36% from the left and 78% from the top).
		// Fall back to the center since that should be a more interesting part
		// of the image than the edges.
		const pos =
			clickEvent && imageRef.current
				? getPointerPositionPoint(imageRef.current, clickEvent)
				: getContainerScrollPosition();
		scrollContainerToPoint(pos ?? { x: 0.5, y: 0.5 });

		if (maxWidth === undefined) {
			onZoomStart?.();
		}
	};

	const zoomOut = () => {
		if (!maxWidth) {
			return;
		}
		setMaxWidth(
			getZoomOutWidth({
				containerHeight: containerDimensionsRef.current.outerHeight,
				containerWidth: containerDimensionsRef.current.outerWidth,
				currentMaxWidth: maxWidth,
				fullImageHeight: height,
				fullImageWidth: width,
			}),
		);

		// See zoomIn for scrolling explanation.
		const pos = getContainerScrollPosition();
		scrollContainerToPoint(pos ?? { x: 0.5, y: 0.5 });
	};

	return (
		// Keyboard events are a bonus, they all have equivalent controls.
		// oxlint-disable-next-line jsx-a11y/no-static-element-interactions
		<div
			className={cnm('relative', className)}
			onKeyDown={
				hasControls
					? (e) => {
							const handlers: Record<string, () => void> = {
								'-': zoomOut,
								'+': zoomIn,
								'ArrowDown': () => panContainer('down'),
								'ArrowLeft': () => panContainer('left'),
								'ArrowRight': () => panContainer('right'),
								'ArrowUp': () => panContainer('up'),
								'Escape': () => {
									resetZoom();
									floatingButtonRef.current?.focus();
								},
							};
							const handler = handlers[e.key];
							if (handler) {
								e.preventDefault();
								e.stopPropagation();
								handler();
							}
						}
					: undefined
			}
		>
			<div className={cn('relative', innerContainerClassName)}>
				<div
					ref={scrollContainerRef}
					onScroll={updateScrollPositionStatus}
					className={cn('no-scrollbar overflow-auto', imageContainerClassName)}
				>
					<div
						className={cn(
							'relative h-full w-full',
							!isZoomed && 'flex items-center justify-center',
							// Using a grid to simulate edge padding since actual margin
							// or padding doesn't work well with overflow.
							isZoomed && [
								'grid',
								"[grid-template-areas:'._._.''._img_.''._._.']",
								'grid-cols-[3vw_1fr_3vw] grid-rows-[3vw_1fr_3vw]',
							],
						)}
						onMouseMove={
							isZoomed
								? (e) => {
										if (scrollContainerRef.current) {
											scrollToMousePosition({
												container: scrollContainerRef.current,
												dimensions: containerDimensionsRef.current,
												mouseEvent: e.nativeEvent,
											});
										}
									}
								: undefined
						}
					>
						{isZoomed && (
							// oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
							<div
								// Mouse event coordinates in the parent listener are based on the
								// element being moused over so we need an overlay that completely
								// covers all the scrollable content. Otherwise the coordinates
								// will jump around when mousing out from the image into the empty
								// areas of the grid.
								className="relative z-1 col-span-full row-span-full cursor-zoom-out"
								onClick={resetZoom}
							/>
						)}
						<Img
							ref={imageRef}
							className={cn(
								isZoomed &&
									'max-w-none cursor-zoom-out self-center justify-self-center [grid-area:img]',
								!isZoomed && 'max-h-full cursor-zoom-in object-contain',
							)}
							style={maxWidth ? { maxWidth } : undefined}
							src={src}
							alt={alt}
							width={width}
							height={height}
							// Default from Next is async, which causes flickering on every
							// render in Chrome (e.g. when zooming).
							decoding="sync"
							onClick={(e) => {
								if (isZoomed) {
									resetZoom();
								} else {
									zoomIn(e.nativeEvent);
								}
							}}
						/>
					</div>
				</div>
				<IconButton
					ref={floatingButtonRef}
					icon={hasControls ? 'close' : 'zoom-in'}
					text={
						hasControls
							? t('general_image_zoom_reset_button_label')
							: t('general_image_zoom_in_button_label')
					}
					variant="transparent"
					onClick={() => {
						if (hasControls) {
							resetZoom();
						} else {
							zoomIn();
						}
					}}
					className="absolute right-r-md bottom-r-md z-2"
				/>
			</div>
			{hasControls && (
				<ImageZoomViewControls
					className="border-t border-subtle bg-base p-4"
					isPanDownDisabled={isAtBottom}
					isPanLeftDisabled={isAtLeft}
					isPanRightDisabled={isAtRight}
					isPanUpDisabled={isAtTop}
					isZoomInDisabled={isFullyZoomed}
					isZoomOutDisabled={!isZoomed}
					onPanDown={() => panContainer('down')}
					onPanLeft={() => panContainer('left')}
					onPanRight={() => panContainer('right')}
					onPanUp={() => panContainer('up')}
					onZoomIn={() => zoomIn()}
					onZoomOut={() => zoomOut()}
				/>
			)}
		</div>
	);
}
ImageZoomView.displayName = 'ImageZoomView';
